// navCatalog.js — Single source of truth for the sidebar navigation tree.
//
// Both Layout.js (desktop sidebar) and MobileBottomNav.js (mobile "All Modules"
// grid) import NAV_CATALOG from here. Adding a new route requires touching only
// this file + App.js routes + backend rolePermissions ALL_MODULES.
//
// SECTION TAXONOMY (2026-06-17 realign): the sidebar uses the same
// SAP-aligned Lines-of-Business spine as the role-access editor
// (moduleCatalog.js) so nav grouping matches how access is granted. Groups
// render in the curated SECTION_ORDER below (not alphabetical) to mirror the
// editor's section sequence. One path = one section.
//
// Shape: same `{ group, founderOnly?, items: [{ label, path, ...flags }] }`
// that Layout.js and MobileBottomNav already consume — zero render-layer changes.
//
// Flags preserved per item (Layout.js uses all; MobileBottomNav ignores unknown):
//   isNew       — renders a "NEW" badge in the sidebar
//   founderOnly — hidden from non-founders in both nav layers
//   financeOnly — hidden from non-finance roles in Layout.js
//   altPaths    — Layout.js active-state matching for related paths (e.g. /salary→Pulse)
//   sortAs      — pin an item next to a sibling without renaming its label

const navSortKey = (value = '') => String(value)
  .replace(/^[^A-Za-z0-9]+/, '')
  .trim()
  .toLocaleLowerCase('en-IN');

// `sortAs` lets an item pin itself next to a sibling (e.g. Benchmark Library
// sits right under Formulations) without renaming its visible label.
const byNavLabel = (a, b) => navSortKey(a.sortAs || a.label || a.group)
  .localeCompare(navSortKey(b.sortAs || b.label || b.group), 'en-IN', { numeric: true });

// Home ('/') always renders first within its group; everything else sorts
// alphabetically (respecting sortAs).
const byNavLabelHomeFirst = (a, b) => {
  if (a.path === '/') return -1;
  if (b.path === '/') return 1;
  return byNavLabel(a, b);
};

// Curated section order — mirrors the SAP-aligned taxonomy in moduleCatalog.js.
const SECTION_ORDER = [
  'Masters',
  'Sales & Orders',
  'Invoicing',
  'Stock',
  'Dispatch',
  'Quality (QMS)',
  'Human Resources',
  'Administration',
];

const sortNavCatalog = (groups) => groups
  .map((group) => ({
    ...group,
    items: [...group.items].sort(byNavLabelHomeFirst),
  }))
  .sort((a, b) => {
    // The unlabeled top block (group '') always renders first, flat (no dropdown).
    if (!a.group) return -1;
    if (!b.group) return 1;
    // Then in curated SECTION_ORDER; any unlisted group falls to the end.
    const ia = SECTION_ORDER.indexOf(a.group); const ib = SECTION_ORDER.indexOf(b.group);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

const NAV_CATALOG_RAW = [
  // Flat top block (group '' = always open)
  { group: '', items: [
    { label: 'Home',              path: '/' },
    { label: 'My Work',           path: '/my-work' },
    { label: 'Tasks',             path: '/tasks' },
    { label: 'Approvals',         path: '/approvals' },
  ]},
  { group: 'Masters', items: [
    { label: 'Products (SKUs)',   path: '/skus' },
    { label: 'Clients',           path: '/clients' },
    { label: 'Vendors',           path: '/vendors' },
    { label: 'Materials',         path: '/rawmaterials' },
  ]},
  { group: 'Sales & Orders', items: [
    { label: 'Quotations',        path: '/quotations' },
    { label: 'Sales Orders',      path: '/sales-orders' },
    { label: 'Order to Cash',     path: '/order-to-cash' },
  ]},
  { group: 'Invoicing', items: [
    { label: 'Sales Invoice',     path: '/sales-invoice' },
    { label: 'Invoices',          path: '/invoices' },
    { label: 'Payments',          path: '/payments' },
    { label: 'Client Ledger',     path: '/client-ledger' },
    { label: 'Credit Notes',      path: '/credit-notes' },
    { label: 'Debit Notes',       path: '/debit-notes' },
    { label: 'AR Ageing',         path: '/ar-aging' },
    { label: 'GST Returns',       path: '/gst-returns' },
  ]},
  { group: 'Stock', items: [
    { label: 'Material Inward',       path: '/rm-inwards' },
    { label: 'Finished Goods Inward', path: '/ready-goods-inwards' },
    { label: 'Warehouse',             path: '/warehouse' },
    { label: 'Finished Goods',        path: '/finished-goods' },
    { label: 'Stock Transfer',        path: '/stock-transfer' },
    { label: 'Inventory Scan',        path: '/inventory-scan' },
    { label: 'Batch Codes',           path: '/batch-codes' },
  ]},
  { group: 'Dispatch', items: [
    { label: 'Dispatch (Gate Pass)', path: '/dispatch-ops' },
    { label: 'Courier',              path: '/courier' },
  ]},
  { group: 'Quality (QMS)', items: [
    { label: 'QC',                path: '/qc' },
    { label: 'Incoming QC',       path: '/qms/incoming' },
    { label: 'In-process QC',     path: '/qms/inprocess' },
    { label: 'Release QC',        path: '/qms/release' },
    { label: 'CAPA',              path: '/capa' },
    { label: 'Complaints',        path: '/complaints' },
  ]},
  { group: 'Human Resources', items: [
    { label: 'HR & Team',         path: '/hr' },
    { label: 'Attendance',        path: '/attendance', altPaths: ['/pulse-documents'] },
    { label: 'Leave',             path: '/leave' },
    { label: 'Salary',            path: '/salary', financeOnly: true },
    { label: 'Payroll',           path: '/payroll', financeOnly: true },
  ]},
  { group: 'Administration', items: [
    { label: 'Users & Roles',     path: '/users' },
    { label: 'Module Access',     path: '/module-access' },
    { label: 'Approval Rules',    path: '/approval-rules' },
    { label: 'Security Center',   path: '/security' },
    { label: 'Announcements',     path: '/settings/announcements' },
    { label: 'Settings',          path: '/settings' },
  ]},
];

export const NAV_CATALOG = sortNavCatalog(NAV_CATALOG_RAW);
